import { WidgetType } from 'widgets/WidgetType';
import { LoggingService } from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import {
    FilteredPortfolioDependentWidgetConfig,
    NotDependentOnPortfolioConfig,
    OrderIdsDependentConfig,
    PortfolioSelectionDependentConfig,
    WidgetStreamConfig,
} from 'widgets/streaming/WidgetStreamConfig';
import State from 'store/State';
import { MessageData } from '@instinet-analytics/core-lib';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import {
    selectPortfolioSelection,
    selectPortfolioStreamingOrderIds,
} from 'portfolio/store/selectors';
import { PortfolioItem } from 'portfolio/PortfolioItem';
import { AppSettingsState } from 'app/store/appSettings/AppSettingsState';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { buildPortfolioSelectionDependentPayload } from 'widgets/store/stream/streamPayloadUtils';
import { PortfolioService } from 'portfolio/PortfolioService';
import throttle from 'lodash/throttle';
import {
    ActionsDeferredDispatcher,
    ActionsDispatcher,
    ActionsImmediateDispatcher,
} from 'store/ActionsDispatcher';
import { using as usingStatement } from 'using-statement';
import { IndigoStore } from 'store/store';
import { MarketDataRequest } from 'marketData/MarketDataRequest';
import { WidgetMarketDataRequestManager } from 'marketData/WidgetMarketDataRequestManager';

export interface WidgetStreamManagerOptions<TPayload, TDataMessage> {
    widgetType: WidgetType;
    widgetKey: string;
    loggingService: LoggingService;
    streamConfig: WidgetStreamConfig<TPayload, TDataMessage>;
    portfolioService: PortfolioService;
    store: IndigoStore;
    marketDataRequestManager: WidgetMarketDataRequestManager;
}

export interface WidgetStreamManagerInfo<TPayload, TDataMessageOrResponse> {
    widgetType: WidgetType;
    widgetKey: string;
    streamConfig: WidgetStreamConfig<TPayload, TDataMessageOrResponse>;
}

/*
 * Base class for WidgetStreamingStreamManager and WidgetRpcStreamManager
 */
export abstract class WidgetStreamManager<
    TPayload,
    TDataMessageOrResponse = unknown,
> {
    public widgetType: WidgetType;
    public widgetKey: string;
    public streamConfig: WidgetStreamConfig<TPayload, TDataMessageOrResponse>;

    private logger: Logger;
    private portfolioService: PortfolioService;
    private marketDataRequestManager: WidgetMarketDataRequestManager;
    private serialisedPayload = '';
    protected store: IndigoStore;
    private invokePotentiallyThrottledCheckStream: (
        dispatcher: ActionsDispatcher,
        reason: string,
        isActive: boolean
    ) => void;

    protected constructor(
        public options: WidgetStreamManagerOptions<
            TPayload,
            TDataMessageOrResponse
        >
    ) {
        const {
            widgetType,
            widgetKey,
            loggingService,
            marketDataRequestManager,
            portfolioService,
            streamConfig,
            store,
        } = options;

        this.logger = buildLogger('WidgetStreamManager', loggingService);
        this.portfolioService = portfolioService;
        this.marketDataRequestManager = marketDataRequestManager;
        this.streamConfig = streamConfig;
        this.store = store;
        this.widgetKey = widgetKey;
        this.widgetType = widgetType;

        this.invokePotentiallyThrottledCheckStream =
            streamConfig.throttleRequestsMs
                ? throttle(
                      this.throttledExecuteStream.bind(this),
                      streamConfig.throttleRequestsMs
                  )
                : this.executeCheckStream.bind(this);
    }

    public dispose() {
        usingStatement(
            new ActionsImmediateDispatcher(this.store),
            (dispatcher) => {
                this.stopStreaming(dispatcher, 'Disposing');
            }
        );
    }

    /*
     * Calculate payload and if has changed update stream
     */
    public checkStream(options: {
        dispatcher: ActionsDispatcher;
        reason: string;
        withoutThrottling?: boolean;
        isActive: boolean;
    }) {
        const { dispatcher, reason, withoutThrottling, isActive } = options;
        if (withoutThrottling) {
            this.executeCheckStream(dispatcher, reason, isActive);
        } else {
            this.invokePotentiallyThrottledCheckStream(
                dispatcher,
                reason,
                isActive
            );
        }
    }

    protected buildPayload(): {
        indigoStreamPayload?: TPayload;
        marketDataRequests?: MarketDataRequest[];
    } {
        const state = this.store.getState();
        const appSettings = selectAppSettings(state);

        if (
            this.streamConfig.configType ===
            'NotDependentOnPortfolioWidgetStreamConfig'
        ) {
            return {
                indigoStreamPayload: buildNotDependentOnPortfolioPayload(
                    this.streamConfig,
                    this.widgetKey,
                    appSettings,
                    state
                ),
            };
        }

        if (
            this.streamConfig.configType ===
            'PortfolioSelectionDependentWidgetStreamConfig'
        ) {
            const selection = selectPortfolioSelection(state);
            const selectionWrapper = new PortfolioSelectionWrapper(selection);
            return {
                indigoStreamPayload: buildPayloadFromPortfolioSelection(
                    this.streamConfig,
                    this.widgetKey,
                    selectionWrapper,
                    appSettings,
                    state
                ),
                marketDataRequests:
                    buildMarketDataRequestsFromPortfolioSelection(
                        this.streamConfig,
                        this.widgetKey,
                        selectionWrapper,
                        appSettings,
                        state
                    ),
            };
        }
        if (
            this.streamConfig.configType ===
            'FilteredPortfolioDependentWidgetStreamConfig'
        ) {
            const filteredPortfolio =
                this.portfolioService.getFilteredPortfolio();
            return {
                indigoStreamPayload: buildPayloadFromFilteredPortfolio(
                    this.streamConfig,
                    this.widgetKey,
                    filteredPortfolio,
                    appSettings,
                    state
                ),
            };
        }
        if (
            this.streamConfig.configType ===
            'OrderIdsDependentWidgetStreamConfig'
        ) {
            // Check the streaming order ids i.e. from Newport...
            const streamingOrderIds = selectPortfolioStreamingOrderIds(state);
            const selection = selectPortfolioSelection(state);
            const selectionWrapper = new PortfolioSelectionWrapper(selection);

            // If the selection has orders eg from Outliers, we use those, otherwise the streaming order ids...
            const orderIds =
                (selection?.selectedOrders ?? []).length > 0
                    ? selection?.selectedOrders.map((s) => s.orderID)
                    : streamingOrderIds;

            return {
                indigoStreamPayload: buildOrderIdsDependentPayload(
                    this.portfolioService.activeInputUsesOrdersStream,
                    this.streamConfig,
                    orderIds ?? [],
                    selectionWrapper,
                    this.widgetKey,
                    appSettings,
                    state
                ),
                marketDataRequests:
                    buildMarketDataRequestsForOrderIdsDependentWidget(
                        this.portfolioService.activeInputUsesOrdersStream,
                        this.streamConfig,
                        orderIds ?? [],
                        selectionWrapper,
                        this.widgetKey,
                        appSettings,
                        state
                    ),
            };
        }

        return {};
    }

    protected onStartStreaming(
        state: State,
        dispatcher: ActionsDispatcher,
        payload: unknown,
        widgetKey: string
    ) {
        if (
            this.streamConfig.configType ===
                'PortfolioSelectionDependentWidgetStreamConfig' &&
            this.streamConfig.onStartStreaming
        ) {
            this.streamConfig.onStartStreaming(
                state,
                dispatcher,
                payload,
                widgetKey
            );
        }
        if (
            this.streamConfig.configType ===
                'FilteredPortfolioDependentWidgetStreamConfig' &&
            this.streamConfig.onStartStreaming
        ) {
            const filteredPortfolio =
                this.portfolioService.getFilteredPortfolio();
            this.streamConfig.onStartStreaming(
                state,
                dispatcher,
                payload,
                widgetKey,
                filteredPortfolio
            );
        }
    }

    private throttledExecuteStream(
        _dispatcher: ActionsDeferredDispatcher,
        reason: string,
        isActive: boolean
    ) {
        usingStatement(
            new ActionsImmediateDispatcher(this.store),
            (dispatcher) => {
                this.executeCheckStream(dispatcher, reason, isActive);
            }
        );
    }

    private executeCheckStream(
        dispatcher: ActionsDispatcher,
        reason: string,
        isActive: boolean
    ) {
        let indigoStreamPayload: TPayload | undefined;
        let marketDataRequests: MarketDataRequest[] | undefined;

        if (isActive) {
            const built = this.buildPayload();
            indigoStreamPayload = built.indigoStreamPayload;
            marketDataRequests = built.marketDataRequests;
        }

        this.marketDataRequestManager.register(
            this.widgetKey,
            marketDataRequests ? marketDataRequests : []
        );

        const serialisedPayload = indigoStreamPayload
            ? JSON.stringify(indigoStreamPayload)
            : '';

        if (serialisedPayload === this.serialisedPayload) {
            this.log('No change', reason);
            return;
        }

        if (serialisedPayload) {
            this.startStreaming(dispatcher, indigoStreamPayload!, reason);
            this.serialisedPayload = serialisedPayload;
            this.onStartStreaming(
                this.store.getState(),
                dispatcher,
                indigoStreamPayload!,
                this.widgetKey
            );
        } else {
            this.stopStreaming(dispatcher, reason);
            this.serialisedPayload = '';
        }
    }

    protected abstract startStreaming(
        dispatcher: ActionsDispatcher,
        payload: TPayload,
        reason: string
    ): void;
    protected abstract stopStreaming(
        dispatcher: ActionsDispatcher,
        reason: string
    ): void;

    protected log(message: string, reason?: string, data?: MessageData) {
        const reasonText = reason ? `(${reason})` : '';
        this.logger.debug(
            `${message} for ${this.widgetType}/${this.widgetKey}/${this.streamConfig.startChannelDestination} ${reasonText}`,
            data
        );
    }

    public getInfo(): WidgetStreamManagerInfo<
        TPayload,
        TDataMessageOrResponse
    > {
        return {
            widgetType: this.widgetType,
            widgetKey: this.widgetKey,
            streamConfig: this.streamConfig,
        };
    }
}

function buildPayloadFromFilteredPortfolio<TPayload>(
    streamConfig: FilteredPortfolioDependentWidgetConfig<TPayload>,
    widgetKey: string,
    filteredPortfolio: PortfolioItem[],
    appSettings: AppSettingsState,
    state: State
): TPayload | undefined {
    return streamConfig.createPayload(
        widgetKey,
        appSettings,
        filteredPortfolio,
        state
    );
}

function buildNotDependentOnPortfolioPayload<TPayload>(
    streamConfig: NotDependentOnPortfolioConfig<TPayload>,
    widgetKey: string,
    appSettings: AppSettingsState,
    state: State
): TPayload | undefined {
    return streamConfig.createPayload(widgetKey, appSettings, state);
}

function buildPayloadFromPortfolioSelection<TPayload>(
    streamConfig: PortfolioSelectionDependentConfig<TPayload>,
    widgetKey: string,
    selection: PortfolioSelectionWrapper,
    appSettings: AppSettingsState,
    state: State
): TPayload | undefined {
    return buildPortfolioSelectionDependentPayload(
        streamConfig.selectionDependencyType,
        streamConfig.createPayload,
        widgetKey,
        selection,
        appSettings,
        state
    );
}

function buildMarketDataRequestsFromPortfolioSelection(
    streamConfig: PortfolioSelectionDependentConfig<unknown>,
    widgetKey: string,
    selection: PortfolioSelectionWrapper,
    appSettings: AppSettingsState,
    state: State
) {
    if (streamConfig.createMarketDataRequests) {
        return streamConfig.createMarketDataRequests(
            selection,
            widgetKey,
            appSettings,
            state
        );
    }
}

function buildOrderIdsDependentPayload<TPayload>(
    activeInputUsesOrdersStream: boolean,
    streamConfig: OrderIdsDependentConfig<TPayload>,
    streamingOrderIds: string[],
    selection: PortfolioSelectionWrapper,
    widgetKey: string,
    appSettings: AppSettingsState,
    state: State
): TPayload | undefined {
    if (streamingOrderIds.length === 0) {
        if (!streamConfig.fallsBackToPortfolioSelection) {
            // If we have no order ids, and the request does not fall back to the portfolio selection, then return undefined to stop the stream
            return undefined;
        }
    }

    // If the current active input does not use the orders stream then this is an override: pass empty order ids to force the stream to use the selection
    const orderIds: string[] = activeInputUsesOrdersStream
        ? streamingOrderIds
        : [];

    return streamConfig.createPayload(
        orderIds,
        selection,
        widgetKey,
        appSettings,
        state
    );
}

function buildMarketDataRequestsForOrderIdsDependentWidget<TPayload>(
    activeInputUsesOrdersStream: boolean,
    streamConfig: OrderIdsDependentConfig<TPayload>,
    streamingOrderIds: string[],
    selection: PortfolioSelectionWrapper,
    widgetKey: string,
    appSettings: AppSettingsState,
    state: State
): MarketDataRequest[] {
    if (!streamConfig.createMarketDataRequests) {
        return [];
    }

    if (streamingOrderIds.length === 0) {
        if (!streamConfig.fallsBackToPortfolioSelection) {
            // If we have no order ids, and the request does not fall back to the portfolio selection, then return undefined to stop the stream
            return [];
        }
    }

    if (streamingOrderIds.length > 0) {
        // We need to wait for the portfolio selection (which is updated when the order is returned from the stream)
        return [];
    }

    return streamConfig.createMarketDataRequests(
        selection,
        widgetKey,
        appSettings,
        state
    );
}
